import { motion } from "framer-motion";
import { ArrowUpRight, Boxes, Sparkles, Truck, Layers } from "lucide-react";
import { Reveal } from "@/lib/motion";

const services = [
  {
    id: "01",
    title: "Custom Booth Design & Fabrication",
    icon: Boxes,
    desc: "From napkin sketch to show floor. Architectural exhibits engineered in-house, built to command attention and last across cities.",
    img: "https://images.unsplash.com/photo-1767294273711-29306e032051?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwZXhoaWJpdGlvbiUyMGJvb3RofGVufDB8fHx8MTc4NDkxNDQ5OXww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-8",
    tall: true,
  },
  {
    id: "02",
    title: "Brand Activations",
    icon: Sparkles,
    desc: "Experiential moments that turn foot traffic into fans.",
    img: "https://images.pexels.com/photos/35120761/pexels-photo-35120761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-4",
    tall: true,
  },
  {
    id: "03",
    title: "Rentals & Logistics",
    icon: Truck,
    desc: "Nationwide freight, storage, install & dismantle. We handle every crate so you never touch a wrench.",
    img: "https://images.pexels.com/photos/33890525/pexels-photo-33890525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-5",
    tall: false,
  },
  {
    id: "04",
    title: "Digital, AV & Additional Services",
    icon: Layers,
    desc: "LED walls, interactive kiosks, graphics production, on-site staffing and lead-capture tech — the full stack under one roof.",
    span: "md:col-span-7",
    tall: false,
    dark: true,
  },
];

function ServiceCard({ s }) {
  const Icon = s.icon;
  return (
    <div
      data-testid={`service-card-${s.id}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 ${s.span} ${
        s.tall ? "min-h-[440px]" : "min-h-[320px]"
      } ${s.dark ? "bg-[#FF3B30]" : "bg-[#0a0a0a]"}`}
    >
      {s.img && (
        <>
          <img
            src={s.img}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-700 group-hover:scale-105 group-hover:opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </>
      )}
      <div className="relative z-10 flex h-full flex-col justify-between p-8 md:p-10">
        <div className="flex items-start justify-between">
          <Icon className="h-8 w-8" strokeWidth={1.4} />
          <span className="font-mono text-xs tracking-[0.2em] text-white/50">{s.id}</span>
        </div>
        <div>
          <h3 className="font-display text-2xl font-semibold leading-tight tracking-tight md:text-3xl">
            {s.title}
          </h3>
          <p className={`mt-4 max-w-md text-sm font-light leading-relaxed ${s.dark ? "text-white/85" : "text-white/60"}`}>
            {s.desc}
          </p>
          <div className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/50 transition-colors group-hover:text-white">
            Learn more
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <section id="services" className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40">
      <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-12 bg-[#FF3B30]" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
              What we do
            </span>
          </div>
          <Reveal>
            <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tighter md:text-6xl lg:text-7xl">
              Full-service<br />by design.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-base font-light leading-relaxed text-white/60">
            One partner for the entire lifecycle — strategy, design, build,
            logistics and on-site execution. No handoffs, no finger-pointing.
          </p>
        </Reveal>
      </div>

      <motion.div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {services.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.06} className={`${s.span} contents md:block`}>
            <ServiceCard s={s} />
          </Reveal>
        ))}
      </motion.div>
    </section>
  );
}
